import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { atomGameState, atomBallsRemaining, atomIsGamePreparing, CharacterChargeState } from '../states/game.atoms';
import { useSound } from '../components/SoundHandler';
import { LAUNCHES_PER_GAME } from '../game-engine';
import { useGameActions } from '../contexts/GameActionsContext';
import { unmuteOnUserPlay } from '../components/YatsuMuteBridge';

const CHARGE_DURATION_MS = 500;

export const usePlayButton = () => {
  const gameState = useAtomValue(atomGameState);
  const ballsRemaining = useAtomValue(atomBallsRemaining);
  const actions = useGameActions();
  const isPreparing = useAtomValue(atomIsGamePreparing);
  const { play: playSound, stop: stopSound } = useSound();

  const [chargeProgress, setChargeProgress] = useState(0);
  const lastChargeRef = useRef<{ status: CharacterChargeState; progress: number }>({ status: 'idle', progress: 0 });
  const chargeControlRef = useRef<{
    active: boolean;
    source: 'keyboard' | 'pointer' | null;
    startTime: number;
    progress: number;
    ready: boolean;
  }>({
    active: false,
    source: null,
    startTime: 0,
    progress: 0,
    ready: false,
  });
  const chargeRafRef = useRef<number | null>(null);
  const spaceHeldRef = useRef(false);
  const pressedRef = useRef(false);
  const pointerPendingChargeRef = useRef(false);
  const queuedPointerDropRef = useRef(false);
  const waitingOnPressStartRef = useRef(false);

  // Sound ref to access current play/stop without dependency
  const soundRef = useRef({ play: playSound, stop: stopSound });
  useEffect(() => {
    soundRef.current = { play: playSound, stop: stopSound };
  }, [playSound, stopSound]);

  const stopChargeSounds = useCallback(() => {
    soundRef.current.stop('playCharge');
    soundRef.current.stop('playChargeMax');
  }, []);
  
  // Use a ref for actions to avoid recreating callbacks when actions change (rarely)
  const actionsRef = useRef(actions);
  useEffect(() => { actionsRef.current = actions; }, [actions]);

  const updateChargeStateWithController = useCallback((status: CharacterChargeState, progress: number) => {
    setChargeProgress(prev => (Math.abs(prev - progress) < 0.001 ? prev : progress));
    const shouldNotify =
      lastChargeRef.current.status !== status ||
      Math.abs(lastChargeRef.current.progress - progress) >= 0.001;

    if (shouldNotify) {
      actionsRef.current?.setCharacterState(status, progress);
    }
    lastChargeRef.current = { status, progress };
  }, []);

  const clearChargeLoop = useCallback(() => {
    if (chargeRafRef.current !== null) {
      cancelAnimationFrame(chargeRafRef.current);
      chargeRafRef.current = null;
    }
  }, []);

  const runChargeTick = useCallback(() => {
    const tick = () => {
      const control = chargeControlRef.current;
      if (!control.active) {
        return;
      }

      const elapsed = performance.now() - control.startTime;
      const progress = Math.max(0, Math.min(elapsed / CHARGE_DURATION_MS, 1));
      control.progress = progress;
      const status: CharacterChargeState = progress >= 1 ? 'ready' : 'charging';
      const becameReady = !control.ready && status === 'ready';
      control.ready = status === 'ready';

      updateChargeStateWithController(status, progress);

      if (becameReady) {
        soundRef.current.play('playChargeMax');
      }

      if (progress < 1) {
        chargeRafRef.current = requestAnimationFrame(tick);
      } else {
        chargeRafRef.current = null;
      }
    };
    tick();
  }, [updateChargeStateWithController]);

  // Stable references for state
  const gameStateRef = useRef(gameState);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  const ballsRemainingRef = useRef(ballsRemaining);
  useEffect(() => { ballsRemainingRef.current = ballsRemaining; }, [ballsRemaining]);

  const isChargeAllowed = useCallback(() => {
    const gs = gameStateRef.current;
    const br = ballsRemainingRef.current;
    if (gs !== 'playing' && gs !== 'bonus') {
      return false;
    }
    return br > 0;
  }, []);

  const startCharge = useCallback((source: 'keyboard' | 'pointer') => {
    const control = chargeControlRef.current;
    if (control.active) {
      return false;
    }
    if (!isChargeAllowed()) {
      return false;
    }

    clearChargeLoop();
    stopChargeSounds();
    control.active = true;
    control.source = source;
    control.startTime = performance.now();
    control.progress = 0;
    control.ready = false;
    updateChargeStateWithController('charging', 0);
    soundRef.current.play('playCharge');
    runChargeTick();
    return true;
  }, [isChargeAllowed, clearChargeLoop, stopChargeSounds, updateChargeStateWithController, runChargeTick]);

  const finishCharge = useCallback((source: 'keyboard' | 'pointer' | null, options: { cancel?: boolean } = {}) => {
    const control = chargeControlRef.current;
    stopChargeSounds();
    if (!control.active) {
      if (source === 'keyboard') {
        spaceHeldRef.current = false;
      }
      return;
    }
    if (source && control.source !== source) {
      return;
    }

    clearChargeLoop();
    const wasReady = control.ready;

    control.active = false;
    control.source = null;
    control.startTime = 0;
    control.progress = 0;
    control.ready = false;

    updateChargeStateWithController('idle', 0);

    if (source === 'keyboard') {
      spaceHeldRef.current = false;
    }

    if (options.cancel) {
      return;
    }

    if (wasReady) {
      soundRef.current.play('playChargeEnd');
      if (ballsRemainingRef.current > 0) {
        actionsRef.current?.dropMultipleBalls(LAUNCHES_PER_GAME);
      }
    } else {
      actionsRef.current?.dropBall();
    }
  }, [clearChargeLoop, stopChargeSounds, updateChargeStateWithController]);

  // Event handlers for GameControls to bind
  const isDisabled = isPreparing || gameState === 'levelup' || gameState === 'gameover' || gameState === 'maxlevel';
  const isWaiting = gameState === 'waiting';

  const handlePointerPressStart = useCallback(() => {
    unmuteOnUserPlay();
    soundRef.current.play('click');
    if (isDisabled || pressedRef.current) {
      return;
    }

    pressedRef.current = true;
    pointerPendingChargeRef.current = false;
    queuedPointerDropRef.current = false;

    if (isWaiting) {
      waitingOnPressStartRef.current = true;
      return;
    }
    waitingOnPressStartRef.current = false;

    const started = startCharge('pointer');
    if (!started) {
      pressedRef.current = false;
    }
  }, [isDisabled, isWaiting, startCharge]);

  const handlePointerPressRelease = useCallback(() => {
    if (!pressedRef.current) {
      return;
    }

    pressedRef.current = false;
    queuedPointerDropRef.current = false;

    if (waitingOnPressStartRef.current) {
      waitingOnPressStartRef.current = false;
      actionsRef.current?.startGame();
      return;
    }

    if (isDisabled) {
      pointerPendingChargeRef.current = false;
      queuedPointerDropRef.current = false;
      return;
    }

    if (pointerPendingChargeRef.current) {
      pointerPendingChargeRef.current = false;
      queuedPointerDropRef.current = true;
      return;
    }

    if (chargeControlRef.current.active) {
      finishCharge('pointer');
      return;
    }

    if (isWaiting) {
      return;
    }

    if (ballsRemainingRef.current <= 0) {
      return;
    }

    actionsRef.current?.dropBall();
  }, [isDisabled, isWaiting, finishCharge]); // ballsRemaining via ref

  const handlePointerPressCancel = useCallback(() => {
    if (!pressedRef.current && !pointerPendingChargeRef.current) {
      queuedPointerDropRef.current = false;
      return;
    }

    pressedRef.current = false;
    pointerPendingChargeRef.current = false;
    queuedPointerDropRef.current = false;
    waitingOnPressStartRef.current = false;

    if (chargeControlRef.current.active) {
      finishCharge('pointer', { cancel: true });
    }
  }, [finishCharge]);

  // Cleanup and lifecycle effects
  useEffect(() => {
    const isSpaceKey = (event: KeyboardEvent) =>
      event.code === 'Space' || event.key === ' ' || event.key === 'Spacebar';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSpaceKey(event)) {
        return;
      }
      unmuteOnUserPlay();
      if (spaceHeldRef.current) {
        event.preventDefault();
        return;
      }
      if (gameStateRef.current === 'waiting') {
        event.preventDefault();
        actionsRef.current?.startGame();
        return;
      }
      const started = startCharge('keyboard');
      if (started) {
        spaceHeldRef.current = true;
        event.preventDefault();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!isSpaceKey(event)) {
        return;
      }
      const control = chargeControlRef.current;
      if (!spaceHeldRef.current && control.source !== 'keyboard') {
        return;
      }
      event.preventDefault();
      finishCharge('keyboard');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [startCharge, finishCharge]);

  useEffect(() => {
    if (!chargeControlRef.current.active) {
      return;
    }
    if ((gameState !== 'playing' && gameState !== 'bonus') || ballsRemaining <= 0) {
      finishCharge(null, { cancel: true });
    }
  }, [ballsRemaining, finishCharge, gameState]);

  useEffect(() => () => {
    clearChargeLoop();
  }, [clearChargeLoop]);

  useEffect(() => {
    if (!pointerPendingChargeRef.current || !pressedRef.current) {
      return;
    }
    if (gameState !== 'playing' && gameState !== 'bonus') {
      return;
    }
    const started = startCharge('pointer');
    if (started) {
      pointerPendingChargeRef.current = false;
    }
  }, [gameState, startCharge]);

  useEffect(() => {
    if (!queuedPointerDropRef.current) {
      return;
    }
    if (gameState !== 'playing' && gameState !== 'bonus') {
      return;
    }
    if (ballsRemaining <= 0) {
      return;
    }
    queuedPointerDropRef.current = false;
    actionsRef.current?.dropBall();
  }, [gameState, ballsRemaining, actions]);

  return {
    chargeProgress,
    handlePointerPressStart,
    handlePointerPressRelease,
    handlePointerPressCancel,
    isPreparing,
  };
};
